import * as THREE from 'three'
import starVertex from '@/shaders/star.vert.glsl?raw'
import starFragment from '@/shaders/star.frag.glsl?raw'
import { celestialBodies } from '@/_constants/bodies'
import { sceneConfig } from '@/_constants/scene-config'
import type { CelestialBody } from '@/_types/celestial-body'

/**
 * A single connection between two bodies, expanded into a contiguous slice of the
 * shared trail point buffer.
 */
interface TrailLink {
  fromId: string
  toId: string
  startIndex: number
  pointCount: number
}

/**
 * Glowing particle streams that connect related services. Every body links to the
 * central sun, and a curated set of cross-service links is layered on top. The
 * streams bow gently and carry a travelling brightness pulse so energy appears to
 * flow between services. Point positions are rebuilt each frame as bodies orbit.
 */
export class ParticleTrails {
  public readonly object: THREE.Points

  private readonly material: THREE.ShaderMaterial
  private readonly positions: THREE.BufferAttribute
  private readonly links: TrailLink[]
  private readonly fractions: number[]
  private readonly fromPosition: THREE.Vector3
  private readonly toPosition: THREE.Vector3
  private readonly bowAxis: THREE.Vector3
  private readonly point: THREE.Vector3

  /**
   * @param {number} pixelRatio - The device pixel ratio used to size points.
   */
  public constructor(pixelRatio: number) {
    this.links = []
    this.fractions = []
    this.fromPosition = new THREE.Vector3()
    this.toPosition = new THREE.Vector3()
    this.bowAxis = new THREE.Vector3()
    this.point = new THREE.Vector3()

    const pairs = this.buildLinkPairs()
    const pointsPerLink = sceneConfig.particles.trailPointsPerLink
    const totalPoints = pairs.length * pointsPerLink

    const positionArray = new Float32Array(totalPoints * 3)
    const colours = new Float32Array(totalPoints * 3)
    const sizes = new Float32Array(totalPoints)
    const phases = new Float32Array(totalPoints)
    const bodyById = new Map<string, CelestialBody>(celestialBodies.map((body) => [body.id, body]))

    let writeIndex = 0
    for (const pair of pairs) {
      const fromBody = bodyById.get(pair.fromId)
      const toBody = bodyById.get(pair.toId)
      if (fromBody === undefined || toBody === undefined) {
        continue
      }
      const fromColour = new THREE.Color(fromBody.accentColour)
      const toColour = new THREE.Color(toBody.accentColour)
      const link: TrailLink = {
        fromId: pair.fromId,
        toId: pair.toId,
        startIndex: writeIndex,
        pointCount: pointsPerLink,
      }
      this.links.push(link)

      for (let step = 0; step < pointsPerLink; step += 1) {
        const fraction = step / (pointsPerLink - 1)
        const colour = fromColour.clone().lerp(toColour, fraction).multiplyScalar(0.85)
        colours[writeIndex * 3] = colour.r
        colours[writeIndex * 3 + 1] = colour.g
        colours[writeIndex * 3 + 2] = colour.b
        sizes[writeIndex] = 1.5
        phases[writeIndex] = -fraction * 14
        this.fractions.push(fraction)
        writeIndex += 1
      }
    }

    const geometry = new THREE.BufferGeometry()
    this.positions = new THREE.BufferAttribute(positionArray, 3)
    this.positions.setUsage(THREE.DynamicDrawUsage)
    geometry.setAttribute('position', this.positions)
    geometry.setAttribute('aColour', new THREE.BufferAttribute(colours, 3))
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))

    this.material = new THREE.ShaderMaterial({
      vertexShader: starVertex,
      fragmentShader: starFragment,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: pixelRatio },
        uTwinkleSpeed: { value: 2.4 },
        uOpacity: { value: 1 },
      },
    })

    this.object = new THREE.Points(geometry, this.material)
    this.object.frustumCulled = false
  }

  /**
   * Rebuild trail point positions for the current frame and advance the flow.
   *
   * @param {number} elapsedSeconds - Total elapsed time since start.
   * @param {Map<string, THREE.Vector3>} bodyPositions - Current world positions by body id.
   * @returns {void}
   */
  public update(elapsedSeconds: number, bodyPositions: Map<string, THREE.Vector3>): void {
    this.material.uniforms.uTime.value = elapsedSeconds

    for (const link of this.links) {
      const from = bodyPositions.get(link.fromId)
      const to = bodyPositions.get(link.toId)
      if (from === undefined || to === undefined) {
        continue
      }
      this.fromPosition.copy(from)
      this.toPosition.copy(to)
      const distance = this.fromPosition.distanceTo(this.toPosition)
      this.bowAxis.subVectors(this.toPosition, this.fromPosition).normalize().cross(THREE.Object3D.DEFAULT_UP)
      if (this.bowAxis.lengthSq() < 0.0001) {
        this.bowAxis.set(1, 0, 0)
      }
      this.bowAxis.normalize()
      const bowAmount = Math.min(distance * 0.12, 16)

      for (let step = 0; step < link.pointCount; step += 1) {
        const bufferIndex = link.startIndex + step
        const fraction = this.fractions[bufferIndex]
        this.point.lerpVectors(this.fromPosition, this.toPosition, fraction)
        const bow = Math.sin(fraction * Math.PI) * bowAmount
        this.point.addScaledVector(this.bowAxis, bow)
        this.positions.setXYZ(bufferIndex, this.point.x, this.point.y, this.point.z)
      }
    }
    this.positions.needsUpdate = true
  }

  /**
   * Set the overall opacity of the trails (dimmed while a planet is focused).
   *
   * @param {number} opacity - Target opacity in the 0..1 range.
   * @returns {void}
   */
  public setOpacity(opacity: number): void {
    this.material.uniforms.uOpacity.value = opacity
  }

  /**
   * Compute the full set of link pairs: sun-to-each visible body plus every
   * curated cross-service connection declared in the constants.
   *
   * @returns {{ fromId: string; toId: string }[]} The list of unique link pairs.
   */
  private buildLinkPairs(): { fromId: string; toId: string }[] {
    const sun = celestialBodies.find((body) => body.tier === 'sun')
    const pairs: { fromId: string; toId: string }[] = []
    const seen = new Set<string>()

    const addPair = (fromId: string, toId: string): void => {
      const key = [fromId, toId].sort().join('::')
      if (seen.has(key)) {
        return
      }
      seen.add(key)
      pairs.push({ fromId, toId })
    }

    for (const body of celestialBodies) {
      if (body.isHidden || body.tier === 'sun') {
        continue
      }
      if (sun !== undefined) {
        addPair(sun.id, body.id)
      }
      for (const connectionId of body.connections) {
        addPair(body.id, connectionId)
      }
    }
    return pairs
  }
}
